import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StaffCombobox } from "@/components/StaffCombobox";
import { Trash2, CircleCheck, CircleDot, Circle, User, Briefcase, CheckCircle2, RotateCcw } from "lucide-react";
import { DueControl, pct } from "./DueControl";

function StageCard({ stage, index, items, isCurrent, editor, onToggle, onSetDue }) {
  const doneCount = items.filter((c) => c.done).length;
  const complete = items.length > 0 && doneCount === items.length;
  const StageIcon = complete ? CircleCheck : isCurrent ? CircleDot : Circle;
  return (
    <div className={`border rounded-sm p-4 ${isCurrent ? "border-primary/60 bg-primary/[0.03]" : "border-border"}`} data-testid="matter-stage">
      <div className="flex items-center gap-2 mb-3">
        <StageIcon className={`h-4 w-4 ${complete ? "text-emerald-600" : isCurrent ? "text-primary" : "text-muted-foreground"}`} />
        <span className="text-sm font-semibold">{index + 1}. {stage.name}</span>
        {isCurrent && <Badge className="text-[10px]">Current</Badge>}
        <span className="text-[11px] text-muted-foreground ml-auto">{doneCount}/{items.length}</span>
      </div>
      <div className="space-y-2 pl-6">
        {items.map((c) => (
          <div key={c.item} data-testid="checklist-item" className="flex items-center gap-2">
            <label className="flex items-start gap-2.5 text-sm cursor-pointer group flex-1 min-w-0">
              <Checkbox data-testid="checklist-checkbox" checked={c.done} disabled={!editor} onCheckedChange={(v) => onToggle(stage.key, c.item, !!v)} className="mt-0.5" />
              <span className={c.done ? "line-through text-muted-foreground" : "group-hover:text-foreground"}>{c.item}</span>
            </label>
            <DueControl c={c} editor={editor} onSet={(due) => onSetDue(stage.key, c.item, due)} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function MatterDetail({ active, stages, editor, actions }) {
  const { assign, setStatus, remove, setStage, toggleItem, setDue } = actions;
  const checklistByStage = useMemo(() => {
    const map = {};
    (active?.checklist || []).forEach((c) => { (map[c.stage] = map[c.stage] || []).push(c); });
    return map;
  }, [active]);

  return (
    <div className="border border-border bg-card rounded-sm p-6 animate-fade-up" data-testid="matter-detail">
      <div className="flex items-start justify-between gap-4 pb-4 mb-4 border-b border-border">
        <div className="min-w-0">
          <div className="text-xs font-condensed font-semibold text-primary">{active.matter_number}</div>
          <h2 className="font-display font-bold text-xl truncate">{active.client_name}</h2>
          <p className="text-sm text-muted-foreground">{active.title}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
            {active.practice_group && <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{active.practice_group}</span>}
            {active.assigned_to && <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{active.assigned_to}</span>}
          </div>
          {editor && (
            <div className="mt-2 w-64" data-testid="assignee-editor">
              <StaffCombobox testid="detail-assignee" value={active.assigned_to} onChange={assign} placeholder="Assign to…" />
            </div>
          )}
        </div>
        {editor && (
          <div className="flex items-center gap-2 shrink-0">
            {active.status === "open"
              ? <Button size="sm" variant="outline" data-testid="close-matter-btn" onClick={() => setStatus("closed")}><CheckCircle2 className="h-4 w-4 mr-1.5" /> Close</Button>
              : <Button size="sm" variant="outline" data-testid="reopen-matter-btn" onClick={() => setStatus("open")}><RotateCcw className="h-4 w-4 mr-1.5" /> Reopen</Button>}
            <button data-testid="delete-matter-btn" onClick={() => remove(active.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1">
          <div className="flex items-center justify-between text-xs mb-1"><span className="text-muted-foreground">Overall progress</span><span className="font-semibold" data-testid="matter-progress">{pct(active.checklist)}%</span></div>
          <Progress value={pct(active.checklist)} className="h-2" />
        </div>
        <div className="w-52">
          <Label className="text-[11px] text-muted-foreground">Current stage</Label>
          <Select value={active.stage} onValueChange={setStage} disabled={!editor}>
            <SelectTrigger data-testid="matter-stage-select" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4" data-testid="matter-stages">
        {stages.map((s, i) => (
          <StageCard key={s.key} stage={s} index={i} items={checklistByStage[s.key] || []} isCurrent={active.stage === s.key} editor={editor} onToggle={toggleItem} onSetDue={setDue} />
        ))}
      </div>
    </div>
  );
}
